<script setup lang="ts">
/*
 * Несоответствия конкретного аудита.
 * Список: GET /api/audit/reports/not-comply/?audits={id}
 */

import { storeToRefs } from 'pinia'
import { computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDisplay, useTheme } from 'vuetify/framework'


import CollapsibleText from '@/components/createdUi/CollapsibleText.vue'
import ShortFio from '@/components/createdUi/ShortFio.vue'
import UniversalFilterTree from '@/components/createdUi/UniversalFilterTree.vue'
import { type IFilterConfig, useTableFilters } from '@/composables/useTableFilters'
import { useTableItemsPerPage } from '@/composables/useTableItemsPerPage'
import { useAuditNonconfrormitiesStore } from '@/modules/audits/stores/useAuditNonconfrormitiesStore/useAuditNonconfrormitiesStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { ERouteNames } from '@/router/ERouteNames'
import { VIconBtn } from 'vuetify/labs/VIconBtn'
import type { IAuditNonconformitiesItem } from '../types/IAuditNonconformitiesItem'

const theme = useTheme()
const { xs } = useDisplay()
const route = useRoute()
const router = useRouter()
const feedbackStore = useFeedbackStore()
const store = useAuditNonconfrormitiesStore()

const { currentSortString, nextCursor } = storeToRefs(store)
const { itemsPerPage } = useTableItemsPerPage('auditNonconformitiesByAudit', 25)

store.itemsPerPage = itemsPerPage.value
watch(itemsPerPage, newVal => {
  store.itemsPerPage = newVal
})

const auditId = computed(() => Number(route.params.id) || 0)

const applyAuditScope = () => {
  if (auditId.value) {
    store.filters.audits = [auditId.value]
  }
}

const resetFiltersKeepingAudit = () => {
  store.resetFilters()
  applyAuditScope()
}

const filterConfigs: IFilterConfig[] = [
  { key: 'standards', labelPrefix: 'Норматив: ', type: 'tree' },
  { key: 'points', labelPrefix: 'Пункт проверки: ', type: 'tree' },
  { key: 'departments', labelPrefix: 'Отделение: ', type: 'tree' },
  { key: 'managers', labelPrefix: 'Зав. отделением: ', type: 'tree' },
  { key: 'status', labelPrefix: 'Статус: ', type: 'tree' },
]

const tableLogic = useTableFilters({
  filters: computed(() => store.filters),
  searchChoices: computed(() => store.searchChoices) as any,
  filterChoices: computed(() => store.filterChoices) as any,
  filterLoading: store.filterLoading,
  fetchFilterChoices: (key: string) => store.fetchFilterChoices(key),
  fetchData: (cursor?: string, limit?: number, throwOnError?: boolean) => store.fetchAudits(cursor, limit, throwOnError),
  realtimeEnabled: true,
  currentSortString,
  itemsPerPage,
  resetFilters: resetFiltersKeepingAudit,
  nextCursor,
  loadedItemsCount: computed(() => store.audits.length),
  totalItemsCount: computed(() => store.totalItemsCount),
  sortMap: {
    status: 'status',
  },
  filterConfigs,
})

interface IAuditNonconformityHeader {
  title: string
  key: string
  sortable?: boolean
  filterType?: string
  filterKey?: string
  align?: 'start' | 'center' | 'end'
}

const headers = computed<IAuditNonconformityHeader[]>(() => [
  { title: 'Аудит', key: 'audit_id', sortable: false },
  { title: 'Тема проверки', key: 'audit_name', sortable: false },
  { title: 'Норматив', key: 'standards', sortable: false, filterType: 'tree', filterKey: 'standards' },
  { title: 'Пункты проверки', key: 'points', sortable: false, filterType: 'tree', filterKey: 'points' },
  { title: 'Отделение', key: 'department', sortable: false, filterType: 'tree', filterKey: 'departments' },
  { title: 'Зав. отделением', key: 'responsible_manager', sortable: false, filterType: 'tree', filterKey: 'managers' },
  { title: 'Отработано', key: 'worked_off_count', sortable: false },
  { title: 'Статус', key: 'status', sortable: false, filterType: 'tree', filterKey: 'status' },
])

const displayedItems = computed<IAuditNonconformitiesItem[]>(() => {
  const start = (tableLogic.currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value

  return store.audits.slice(start, end)
})

const standardLabels = computed(() => {
  const labels: string[] = []
  const seen = new Set<string>()

  for (const item of store.audits) {
    if (item.standards?.length) {
      for (const s of item.standards) {
        const label = s.label?.trim()
        if (label && !seen.has(label)) {
          seen.add(label)
          labels.push(label)
        }
      }
    }
  }

  return labels
})

const standardsTitle = computed(() =>
  standardLabels.value.length > 1 ? 'Нормативы' : 'Норматив',
)

const standardLabel = computed(() =>
  standardLabels.value.length ? standardLabels.value.join(', ') : '',
)

const formatLabelList = (items?: { label: string }[]) =>
  (items || []).map(i => i.label).join(', ')

const handleRowClick = async (item: IAuditNonconformitiesItem, event: Event) => {
  const target = event.target as HTMLElement
  if (target.closest('.v-chip, .v-btn, button, a, .v-data-table__th')) {
    return
  }

  if (!item?.id || !auditId.value) return

  await router.push({
    name: ERouteNames.AUDIT_NONCONFORMITY_DETAIL,
    params: {
      id: String(auditId.value),
      reportId: String(item.id),
    },
  })
}

const rowProps = (data: { item: IAuditNonconformitiesItem }) => ({
  onClick: (event: Event) => handleRowClick(data.item, event),
})

onMounted(async () => {
  resetFiltersKeepingAudit()
  await store.fetchAudits()
})

onUnmounted(() => {
  store.resetFilters()
})
</script>

<template>
  <div class="d-flex flex-column gap-4">
    <div class="text-h6 font-weight-medium text-high-emphasis">
      {{ standardsTitle }}: {{ standardLabel }}
    </div>

    <div class="wrapper">
      <div>
        <div
          class="d-flex align-center gap-4"
          :class="[
            tableLogic.activeFilterChips.value.length ? 'px-4 pt-4' : 'pa-4',
            { 'flex-column': xs },
          ]"
        >
          <div
            class="d-flex align-center gap-2 w-100"
            :class="{ 'flex-column': xs }"
          >
            <VTextField
              v-model="tableLogic.searchFilter.value"
              :class="xs ? 'w-100' : 'flex-grow-0 w-25'"
              variant="outlined"
              clearable
              placeholder="Фильтр по ключевому слову"
              autofocus
              autocomplete="off"
              autocapitalize="off"
              autocorrect="off"
              spellcheck="false"
              type="text"
              @keyup.enter="tableLogic.applySearchFilter()"
            />

            <VIconBtn
              v-if="tableLogic.searchFilter.value"
              color="primary"
              class="rounded-sm"
              icon="ri-filter-2-line"
              variant="outlined"
              :rounded="false"
              width="48"
              height="48"
              @click="tableLogic.applySearchFilter()"
            />

            <VBtn
              v-if="tableLogic.hasActiveFilters.value"
              :class="{ 'w-100': xs }"
              prepend-icon="ri-delete-bin-line"
              variant="outlined"
              color="secondary"
              class="text-color-secondary"
              height="48"
              :loading="feedbackStore.isGlobalLoading"
              @click="tableLogic.renewDataAndResetFilters()"
            >
              Сбросить фильтры
            </VBtn>

            <div
              class="d-flex align-center gap-2"
              :class="[
                { 'w-100 flex-column': xs },
                !xs && 'ml-auto',
              ]"
            >
<!--               <VBtn
                :class="{ 'w-100': xs }"
                variant="outlined"
                color="secondary"
                prepend-icon="ri-upload-2-line"
              >
                Отчет
              </VBtn>
              <VBtn
                :class="{ 'w-100': xs }"
                variant="outlined"
                color="secondary"
                prepend-icon="ri-upload-2-line"
              >
                Протокол
              </VBtn> -->
            </div>
          </div>
        </div>

        <div
          v-if="tableLogic.activeFilterChips.value.length"
          class="filter-chip d-flex ml-4 align-center gap-2 py-3"
        >
          <VChip
            v-for="chip in tableLogic.activeFilterChips.value"
            :key="`${chip.type}-${chip.value || 'no-value'}`"
            closable
            @click:close="tableLogic.removeFilter(chip)"
          >
            {{ chip.label }}
          </VChip>
        </div>

        <VDataTableServer
          :items="displayedItems"
          :items-length="store.totalItemsCount"
          :headers="headers as any"
          :items-per-page="itemsPerPage"
          :row-props="rowProps"
          class="no-row-gaps custom-data-table custom-data-table-footer audit-nonconformities-table"
          item-value="id"
          :class="[{ 'dark-theme': theme.global.current.value.dark }]"
          @update:options="tableLogic.handlePageChange"
        >
          <template #no-data>
            <div class="text-center text-italic py-4">
              Данные отсутствуют
            </div>
          </template>

          <template
            v-for="col in headers.filter(h => h.filterType)"
            :key="col.key"
            #[`header.${col.key}`]="{ column }"
          >
            <div class="header-content d-flex align-center">
              <span>{{ column.title }}</span>

              <VIcon
                v-if="column.sortable"
                size="16"
                class="ml-1 custom-sort-icon"
                :icon="tableLogic.getSortIcon(col.key)"
                :class="{
                  'is-active': tableLogic.isSortActive(col.key),
                  'is-desc': tableLogic.isSortDesc(col.key),
                }"
              />

              <div
                v-if="tableLogic.filterLoading?.[col.filterKey!]"
                class="d-flex align-center justify-center ml-1"
                style="width: 28px; height: 28px"
              >
                <VProgressCircular
                  indeterminate
                  color="primary"
                  size="20"
                  width="2"
                />
              </div>

              <VIconBtn
                v-else
                variant="plain"
                size="small"
                class="filter-icon-btn"
                :icon="tableLogic.getFilterIcon(col.filterKey!)"
                @click.stop="tableLogic.handleFilterClick(col.filterKey!)"
              />

              <VMenu
                v-model="tableLogic.filterMenus.value[col.filterKey!]"
                :close-on-content-click="false"
                location="bottom start"
                transition="scale-transition"
              >
                <template #activator="{ props: menuProps }">
                  <div v-bind="menuProps" />
                </template>
                <UniversalFilterTree
                  :field-key="col.filterKey!"
                  :store="store"
                  :model-value="tableLogic.tempSelectedFilters.value[col.filterKey!] || []"
                  :placeholder="`Поиск: ${column.title}`"
                  @update:model-value="tableLogic.tempSelectedFilters.value[col.filterKey!] = $event"
                  @apply="tableLogic.applyFilter(col.filterKey!)"
                />
              </VMenu>
            </div>
          </template>

          <!-- DATA: Аудит -->
          <template #[`item.audit_id`]="{ item }">
            <span>{{ item.audit_id }}</span>
          </template>

          <template #[`item.audit_name`]="{ item }">
            <CollapsibleText
              v-if="item.audit_name"
              :text="item.audit_name"
            />
            <span v-else />
          </template>

          <template #[`item.standards`]="{ item }">
            <CollapsibleText
              v-if="item.standards?.length"
              :text="formatLabelList(item.standards)"
            />
            <span v-else />
          </template>

          <template #[`item.points`]="{ item }">
            <CollapsibleText
              v-if="item.points?.length"
              :text="formatLabelList(item.points)"
            />
            <span v-else />
          </template>

          <template #[`item.department`]="{ item }">
            <span>{{ item.department?.name || '' }}</span>
          </template>

          <template #[`item.responsible_manager`]="{ item }">
            <ShortFio
              :person="item.responsible_manager"
              empty-text=""
            />
          </template>

          <template #[`item.worked_off_count`]="{ item }">
            <span>{{ item.worked_off_count }}/{{ item.findings_count }}</span>
          </template>

          <template #[`item.status`]="{ item }">
            <VChip
              class="status-badge"
              :class="item.status"
              size="small"
            >
              {{ item.status_label || item.status }}
            </VChip>
          </template>

          <template #bottom>
            <div
              class="custom-data-table-footer"
              :class="{ 'hide-next-button': tableLogic.hideNextButton.value }"
            >
              <VDataTableFooter
                :show-current-page="true"
                :items-per-page-options="[25, 50, 100]"
                v-bind="$attrs"
                @click:prev="$emit('click:prev', $event)"
                @click:next="$emit('click:next', $event)"
                @click:first="$emit('click:first', $event)"
                @click:last="$emit('click:last', $event)"
              />
            </div>
          </template>
        </VDataTableServer>
      </div>
    </div>
    <RouterView />
  </div>
</template>

<style scoped lang="scss">
:deep(.custom-data-table .v-data-table__th) {
  background-color: rgb(var(--v-theme-primary-100), var(--v-overlay-scrim-opacity)) !important;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity)) !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
  line-height: 22px !important;
  position: relative;
  padding-inline: 1rem;
  white-space: nowrap;
}

:deep(.custom-data-table .v-data-table__td) {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  padding-inline: 1rem;
  padding-block: 0.75rem !important;
}

:deep(.custom-data-table .v-data-table__th:not(:last-child))::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  width: 2px;
  height: 14px;
  background-color: rgb(var(--v-theme-on-background), var(--v-custom-opacity));
}

:deep(.custom-data-table td),
:deep(.custom-data-table th) {
  overflow: visible;
  white-space: normal;
  word-wrap: break-word;
}

:deep(.custom-data-table tr:hover) {
  box-shadow: 0 2px 4px rgb(var(--v-shadow-key-umbra-color), var(--v-shadow-xs-opacity));
  cursor: pointer;
  background-color: var(--table-row-hover) !important;
}

:deep(.custom-data-table.dark-theme tr:hover) {
  box-shadow: 0 2px 4px rgba(99, 118, 135, 0.2) !important;
}

:deep(.v-data-table-footer) {
  display: flex;
  justify-content: center;
  align-items: center;
}

.custom-data-table-footer {
  :deep(.v-data-table-footer) {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;

    .v-data-table-footer__items-per-page > span:first-child {
      display: none !important;
    }

    .v-data-table-footer__pagination {
      order: 1;
    }

    .v-data-table-footer__items-per-page {
      order: 2;
    }

    .v-data-table-footer__info {
      order: 3;
    }

    .v-pagination__last > button,
    .v-pagination__next > button[aria-disabled="true"] {
      display: none !important;
    }
  }
}

:deep(tbody .v-data-table__tr .v-data-table__td:first-child) {
  color: rgb(var(--v-theme-primary-500)) !important;
}

:deep(.v-data-table-header__sort-icon) {
  display: none !important;
}

.custom-sort-icon {
  font-size: 16px;
  transition: transform 0.3s ease, opacity 0.2s ease, color 0.2s ease;
  opacity: 0.15;
  display: inline-block;
  vertical-align: middle;
  cursor: pointer;

  &.is-active {
    opacity: 1 !important;
    color: rgb(var(--v-theme-primary)) !important;
  }

  &.is-desc {
    transform: rotate(180deg);
  }
}

:deep(.v-data-table__th--sortable:hover) .custom-sort-icon {
  opacity: 0.6;
}

/* Ширины колонок nonconformities: компактные для коротких данных, расширенные для длинных текстов */
:deep(.audit-nonconformities-table th:nth-child(1)),
:deep(.audit-nonconformities-table td:nth-child(1)) {
  width: 5rem !important;
  min-width: 5rem !important;
}

:deep(.audit-nonconformities-table th:nth-child(2)),
:deep(.audit-nonconformities-table td:nth-child(2)) {
  width: 14rem !important;
  min-width: 12rem !important;
}

:deep(.audit-nonconformities-table th:nth-child(3)),
:deep(.audit-nonconformities-table td:nth-child(3)) {
  width: 10rem !important;
  min-width: 9rem !important;
}

:deep(.audit-nonconformities-table th:nth-child(4)),
:deep(.audit-nonconformities-table td:nth-child(4)) {
  min-width: 25rem !important;
}

:deep(.audit-nonconformities-table th:nth-child(5)),
:deep(.audit-nonconformities-table td:nth-child(5)) {
  width: 14rem !important;
  min-width: 12rem !important;
}

:deep(.audit-nonconformities-table th:nth-child(6)),
:deep(.audit-nonconformities-table td:nth-child(6)) {
  width: 11rem !important;
  min-width: 10rem !important;
}

:deep(.audit-nonconformities-table th:nth-child(7)),
:deep(.audit-nonconformities-table td:nth-child(7)) {
  width: 6.5rem !important;
  min-width: 6.5rem !important;
}

:deep(.audit-nonconformities-table th:nth-child(8)),
:deep(.audit-nonconformities-table td:nth-child(8)) {
  width: 9rem !important;
  min-width: 9rem !important;
}

.status-badge {
  font-weight: 500;
  padding-inline: 6px;
}

.filter-chip {
  overflow-x: auto;
  white-space: nowrap;
}

.filter-chip .v-chip {
  flex-shrink: 0;
  max-width: none !important;
  white-space: nowrap;
  overflow: visible;
}
</style>
